<script setup lang="ts">
import { ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldPartTemplate } from "@/types/field-observations.types";
import type { ItemDisplayMode } from "@/stores/Settings";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import DRDisplayItem from "@/components/UI/items/DRDisplayItem.vue";
import ConfirmationModal from "@/components/generic/ConfirmationModal.vue";
import FieldPartTemplateTreePreview from "./FieldPartTemplateTreePreview.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import CreateTemplateDrawer from "./CreateTemplateDrawer.vue";
import { useDeleteFieldPartTemplate } from "@/stores/queries/field-observations.query";

const props = defineProps<{
  template: FieldPartTemplate;
  display: ItemDisplayMode;
}>();

const { t } = useTranslation();

const showDeleteConfirm = ref(false);
const showPreview = ref(false);
const showEditDrawer = ref(false);

const { mutate: deleteTemplate, isPending: isDeleting } =
  useDeleteFieldPartTemplate();

const partsCount = computed(() => {
  const countNodes = (
    nodes: { name: string; children: { name: string; children: any[] }[] }[]
  ): number => {
    return nodes.reduce(
      (acc, node) => acc + 1 + countNodes(node.children || []),
      0
    );
  };
  return countNodes(props.template.tree.parts);
});

const handleDelete = () => {
  deleteTemplate(props.template.id);
  showDeleteConfirm.value = false;
};

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString();
};

const togglePreview = (event: Event) => {
  event.stopPropagation();
  showPreview.value = !showPreview.value;
};
</script>

<template>
  <DRDisplayItem
    :display="display"
    :title="template.name"
    :subtitle="template.description"
    :extended="showPreview"
    @click="togglePreview"
  >
    <template #icon="{ size }">
      <PhosphorIcon name="tree-structure" :size="size" />
    </template>

    <template #more>
      <span class="text-grey">
        <i class="bi-folder2 me-1"></i>
        {{
          t("dashboard:fieldObservations.partTemplates.partsCount", {
            count: partsCount,
          })
        }}
      </span>
      <span class="text-grey">
        <i class="bi-calendar me-1"></i>
        {{ formatDate(template.createdAt) }}
      </span>
    </template>

    <template #actions>
      <DRBtnIcon
        :size="28"
        :icon="showPreview ? 'bi-chevron-up' : 'bi-chevron-down'"
        variant="flat"
        @click="togglePreview"
      />
      <el-dropdown trigger="click" @click.stop>
        <DRBtnIcon :size="28" icon="bi-three-dots" variant="flat" @click.stop />
        <template #dropdown>
          <el-dropdown-menu>
            <DRDropdownItem
              icon="bi-pencil"
              :label="t('common:edit')"
              @select="showEditDrawer = true"
            />
            <DRDropdownItem
              icon="bi-trash"
              :label="t('common:delete')"
              type="danger"
              @select="showDeleteConfirm = true"
            />
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </template>

    <template #extended>
      <div class="p-3">
        <FieldPartTemplateTreePreview :nodes="template.tree.parts" />
      </div>
    </template>
  </DRDisplayItem>

  <ConfirmationModal
    v-model="showDeleteConfirm"
    :title="t('dashboard:fieldObservations.partTemplates.deleteConfirm.title')"
    :message="
      t('dashboard:fieldObservations.partTemplates.deleteConfirm.message')
    "
    :confirm-label="t('common:delete')"
    :loading="isDeleting"
    danger
    @confirm="handleDelete"
  />

  <CreateTemplateDrawer v-model="showEditDrawer" :template="template" />
</template>
